/**
 * Routines admin page — manage internal recurring task templates
 * (Office / Warehouse / Laundry daily tasks).
 * Visible to admin & coordinator.
 */
import { useEffect, useState, useCallback } from "react";
import { Plus, Trash2, Edit3, Repeat2, RefreshCw, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Modal from "@/components/Modal";

const DAY_LABELS = ["L", "M", "X", "J", "V", "S", "D"];
const FULL_DAY_LABELS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

function formatDuration(totalMin) {
    const m = Math.max(0, Number(totalMin) || 0);
    const h = Math.floor(m / 60);
    const r = m % 60;
    if (h === 0) return `${r} min`;
    if (r === 0) return `${h} h`;
    return `${h} h ${r} min`;
}

const EMPTY_FORM = {
    name: "",
    description: "",
    days_of_week: [0, 1, 2, 3, 4],
    time: "09:00",
    estimated_minutes: 30,
    property_id: "",
    assigned_to: "",
    checklist: [""],
    active: true,
    priority: "medium",
};

export default function Routines() {
    const [items, setItems] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [editing, setEditing] = useState(null); // template or null
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [seeding, setSeeding] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            api.get("/routine-templates"),
            api.get("/employees?active=true"),
            api.get("/properties"),
        ])
            .then(([r, e, p]) => {
                setItems(r.data || []);
                setEmployees(e.data || []);
                setProperties(p.data || []);
            })
            .catch((err) => setError(formatApiError(err)))
            .finally(() => setLoading(false));
    }, []);
    useEffect(load, [load]);

    const openCreate = () => {
        const office = properties.find((p) => p.code === "OFICINA-01");
        setEditing({});
        setForm({ ...EMPTY_FORM, property_id: office?.id || "" });
    };
    const openEdit = (tpl) => {
        setEditing(tpl);
        setForm({
            name: tpl.name || "",
            description: tpl.description || "",
            days_of_week: tpl.days_of_week || [],
            time: tpl.time || "09:00",
            estimated_minutes: tpl.estimated_minutes || 30,
            property_id: tpl.property_id || "",
            assigned_to: tpl.assigned_to || "",
            checklist: (tpl.checklist && tpl.checklist.length) ? tpl.checklist : [""],
            active: tpl.active !== false,
            priority: tpl.priority || "medium",
        });
    };
    const closeModal = () => { setEditing(null); setForm(EMPTY_FORM); };

    const toggleDay = (d) => {
        setForm((f) => ({
            ...f,
            days_of_week: f.days_of_week.includes(d)
                ? f.days_of_week.filter((x) => x !== d)
                : [...f.days_of_week, d].sort(),
        }));
    };

    const save = async () => {
        const payload = {
            ...form,
            estimated_minutes: Number(form.estimated_minutes) || 30,
            assigned_to: form.assigned_to || null,
            property_id: form.property_id || null,
            checklist: form.checklist.map((s) => s.trim()).filter(Boolean),
        };
        if (!payload.name.trim()) {
            alert("El nombre es obligatorio");
            return;
        }
        setSaving(true);
        try {
            if (editing && editing.id) {
                await api.patch(`/routine-templates/${editing.id}`, payload);
            } else {
                await api.post("/routine-templates", payload);
            }
            closeModal();
            load();
        } catch (err) {
            alert(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (tpl) => {
        if (!window.confirm(`¿Eliminar la rutina "${tpl.name}"?`)) return;
        try {
            await api.delete(`/routine-templates/${tpl.id}`);
            load();
        } catch (err) {
            alert(formatApiError(err));
        }
    };

    const seedToday = async () => {
        setSeeding(true);
        try {
            const { data } = await api.post("/routine-templates/seed-today");
            alert(`Hoy: ${data.templates_active_today} plantillas activas · ${data.tasks_created} nuevas tareas creadas.`);
        } catch (err) {
            alert(formatApiError(err));
        } finally {
            setSeeding(false);
        }
    };

    if (loading) return <div className="zentra-skeleton h-72" data-testid="routines-loading" />;

    return (
        <div className="space-y-6" data-testid="routines-page">
            {error && (
                <div className="rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-900 inline-flex items-center gap-2">
                    <AlertCircle size={14} /> {error}
                </div>
            )}

            {/* Header */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <p className="text-[10px] font-semibold tracking-widest text-zentra-accent-strong uppercase">
                        Operaciones internas
                    </p>
                    <h1 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1 inline-flex items-center gap-2">
                        <Repeat2 size={24} /> Rutinas internas
                    </h1>
                    <p className="text-sm text-zentra-muted mt-1 max-w-2xl">
                        Plantillas de tareas recurrentes para almacén, oficina y lavandería. Cada
                        día a las 06:00 UTC el sistema crea las tareas asignadas a su responsable.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={seedToday} disabled={seeding} className="zentra-btn-ghost" data-testid="routines-seed-today">
                        <RefreshCw size={14} className={seeding ? "animate-spin" : ""} /> {seeding ? "Generando…" : "Generar las de hoy"}
                    </button>
                    <button onClick={openCreate} className="zentra-btn-primary" data-testid="routines-create">
                        <Plus size={16} /> Nueva rutina
                    </button>
                </div>
            </div>

            {/* Empty state */}
            {items.length === 0 && (
                <div className="zentra-card-static p-10 text-center">
                    <Repeat2 size={36} className="mx-auto text-zentra-muted/60" />
                    <p className="mt-3 font-display font-semibold text-zentra-ink">Sin rutinas configuradas</p>
                    <p className="text-sm text-zentra-muted mt-1">
                        Crea una plantilla para empezar (ej.: Apertura almacén, Cierre, Lavandería).
                    </p>
                    <button onClick={openCreate} className="zentra-btn-primary mt-4">
                        <Plus size={16} /> Crear primera rutina
                    </button>
                </div>
            )}

            {/* List */}
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map((tpl) => {
                    const emp = employees.find((e) => e.id === tpl.assigned_to);
                    return (
                        <div key={tpl.id} className="zentra-card-static p-5 flex flex-col gap-3" data-testid={`routine-card-${tpl.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <h3 className="font-display font-semibold text-zentra-ink truncate">{tpl.name}</h3>
                                    {tpl.description && (
                                        <p className="text-xs text-zentra-muted mt-1 line-clamp-2">{tpl.description}</p>
                                    )}
                                </div>
                                <span
                                    className={`text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded-full ${
                                        tpl.active
                                            ? "bg-emerald-100 text-emerald-900 border border-emerald-300"
                                            : "bg-zinc-200 text-zinc-700 border border-zinc-300"
                                    }`}
                                >
                                    {tpl.active ? "Activa" : "Pausada"}
                                </span>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 text-[11px]">
                                <span className="font-mono-zentra px-2 py-1 rounded-md bg-zentra-bg border border-zentra-border">
                                    {tpl.time}
                                </span>
                                <span className="text-zentra-muted">·</span>
                                <span className="text-zentra-ink">{formatDuration(tpl.estimated_minutes || 30)}</span>
                                <span className="text-zentra-muted">·</span>
                                <span className="text-zentra-ink truncate max-w-[140px]">
                                    {emp ? emp.name : <span className="text-zentra-muted italic">Sin asignar</span>}
                                </span>
                            </div>
                            <div className="flex gap-1">
                                {DAY_LABELS.map((lbl, i) => (
                                    <span
                                        key={i}
                                        className={`text-[10px] w-6 h-6 inline-flex items-center justify-center rounded font-bold ${
                                            (tpl.days_of_week || []).includes(i)
                                                ? "bg-zentra-primary text-white"
                                                : "bg-zentra-bg text-zentra-muted border border-zentra-border"
                                        }`}
                                    >
                                        {lbl}
                                    </span>
                                ))}
                            </div>
                            {tpl.checklist && tpl.checklist.length > 0 && (
                                <div className="pt-2 border-t border-zentra-border">
                                    <p className="text-[10px] font-semibold tracking-widest uppercase text-zentra-muted mb-1.5">
                                        Checklist · {tpl.checklist.length} pasos
                                    </p>
                                    <ul className="text-xs text-zentra-ink space-y-0.5 max-h-24 overflow-y-auto">
                                        {tpl.checklist.slice(0, 4).map((c, i) => (
                                            <li key={i} className="truncate">· {c}</li>
                                        ))}
                                        {tpl.checklist.length > 4 && (
                                            <li className="text-zentra-muted text-[10px]">… y {tpl.checklist.length - 4} más</li>
                                        )}
                                    </ul>
                                </div>
                            )}
                            <div className="flex justify-end gap-2 pt-2 border-t border-zentra-border">
                                <button onClick={() => openEdit(tpl)} className="zentra-btn-ghost text-xs" data-testid={`routine-edit-${tpl.id}`}>
                                    <Edit3 size={13} /> Editar
                                </button>
                                <button onClick={() => remove(tpl)} className="zentra-btn-ghost text-xs text-red-700 hover:bg-red-50" data-testid={`routine-delete-${tpl.id}`}>
                                    <Trash2 size={13} /> Borrar
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Form modal */}
            <Modal
                open={!!editing}
                onClose={closeModal}
                title={editing && editing.id ? "Editar rutina" : "Nueva rutina"}
                subtitle="Se creará una tarea automática cada día activo a la hora indicada."
                wide
                testId="routine-modal"
                footer={
                    <>
                        <button onClick={closeModal} className="zentra-btn-ghost" data-testid="routine-cancel">Cancelar</button>
                        <button onClick={save} disabled={saving} className="zentra-btn-primary" data-testid="routine-save">
                            {saving ? "Guardando…" : "Guardar"}
                        </button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className="zentra-label">Nombre</label>
                            <input
                                className="zentra-input"
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Apertura almacén"
                                data-testid="routine-name"
                            />
                        </div>
                        <div>
                            <label className="zentra-label">Hora de inicio</label>
                            <input
                                type="time"
                                className="zentra-input"
                                value={form.time}
                                onChange={(e) => setForm({ ...form, time: e.target.value })}
                                data-testid="routine-time"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="zentra-label">Descripción</label>
                        <textarea
                            className="zentra-input"
                            rows={2}
                            value={form.description}
                            onChange={(e) => setForm({ ...form, description: e.target.value })}
                            placeholder="Resumen breve de la rutina"
                        />
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                        <div>
                            <label className="zentra-label">Duración estimada</label>
                            <div className="flex items-center gap-2">
                                <div className="flex-1 relative">
                                    <input
                                        type="number"
                                        min="0"
                                        max="23"
                                        className="zentra-input pr-10"
                                        value={Math.floor((Number(form.estimated_minutes) || 0) / 60)}
                                        onChange={(e) => {
                                            const hours = Math.max(0, Math.min(23, Number(e.target.value) || 0));
                                            const mins = (Number(form.estimated_minutes) || 0) % 60;
                                            setForm({ ...form, estimated_minutes: hours * 60 + mins });
                                        }}
                                        data-testid="routine-hours"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zentra-muted font-semibold">h</span>
                                </div>
                                <div className="flex-1 relative">
                                    <input
                                        type="number"
                                        min="0"
                                        max="59"
                                        step="5"
                                        className="zentra-input pr-12"
                                        value={(Number(form.estimated_minutes) || 0) % 60}
                                        onChange={(e) => {
                                            const mins = Math.max(0, Math.min(59, Number(e.target.value) || 0));
                                            const hours = Math.floor((Number(form.estimated_minutes) || 0) / 60);
                                            setForm({ ...form, estimated_minutes: hours * 60 + mins });
                                        }}
                                        data-testid="routine-minutes"
                                    />
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zentra-muted font-semibold">min</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="zentra-label">Responsable</label>
                            <select
                                className="zentra-input"
                                value={form.assigned_to || ""}
                                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                                data-testid="routine-assignee"
                            >
                                <option value="">— Sin asignar —</option>
                                {employees.map((e) => (
                                    <option key={e.id} value={e.id}>{e.name} ({e.role})</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="zentra-label">Propiedad / sede</label>
                            <select
                                className="zentra-input"
                                value={form.property_id || ""}
                                onChange={(e) => setForm({ ...form, property_id: e.target.value })}
                                data-testid="routine-property"
                            >
                                <option value="">— OFICINA-01 por defecto —</option>
                                {properties.map((p) => (
                                    <option key={p.id} value={p.id}>{p.code} · {p.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div>
                        <label className="zentra-label">Días de la semana</label>
                        <div className="flex gap-1.5 flex-wrap">
                            {FULL_DAY_LABELS.map((lbl, i) => {
                                const on = form.days_of_week.includes(i);
                                return (
                                    <button
                                        type="button"
                                        key={i}
                                        onClick={() => toggleDay(i)}
                                        className={`px-3 py-1.5 text-xs font-bold rounded-md border transition ${
                                            on
                                                ? "bg-zentra-primary text-white border-zentra-primary"
                                                : "bg-white text-zentra-ink border-zentra-border hover:border-zentra-primary"
                                        }`}
                                        data-testid={`routine-day-${i}`}
                                    >
                                        {lbl}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <label className="zentra-label">Checklist</label>
                        <div className="space-y-1.5">
                            {form.checklist.map((c, i) => (
                                <div key={i} className="flex gap-2">
                                    <input
                                        className="zentra-input flex-1"
                                        value={c}
                                        onChange={(e) => {
                                            const cl = [...form.checklist];
                                            cl[i] = e.target.value;
                                            setForm({ ...form, checklist: cl });
                                        }}
                                        placeholder={`Paso ${i + 1}`}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setForm({ ...form, checklist: form.checklist.filter((_, j) => j !== i) })}
                                        className="zentra-btn-ghost px-2"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => setForm({ ...form, checklist: [...form.checklist, ""] })}
                                className="zentra-btn-ghost text-xs"
                                data-testid="routine-add-step"
                            >
                                <Plus size={12} /> Añadir paso
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 pt-3 border-t border-zentra-border">
                        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.active}
                                onChange={(e) => setForm({ ...form, active: e.target.checked })}
                                data-testid="routine-active"
                            />
                            Activa
                        </label>
                        <span className="text-zentra-muted text-xs">Si la pausas, no se generarán tareas.</span>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
